import RightSideDetailsPanel, { COLUMN } from "Areas/SharedFeatures/rightSideDetailsPanelFloat";
import getId from "Areas/SharedFeatures/types/utils/getId";
import useBeforeUnloadMessage from "Common/componentUtils/useBeforeUnloadMessage";
import useHistory from "Common/componentUtils/useHistory";
import useKeyboardEventListener from "Common/componentUtils/useKeyboardEventListener";
import Menu from "Components/menu";
import ProgressOverlay from "Components/progressOverlay";
import { themeVariables } from "Components/theme";
import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import difference from "lodash/difference";
import filter from "lodash/filter";
import find from "lodash/find";
import head from "lodash/head";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import map from "lodash/map";
import MapBrowserEventType from "ol/MapBrowserEventType";
import { Attribution } from "ol/control";
import { never, shiftKeyOnly } from "ol/events/condition";
import { boundingExtent } from "ol/extent";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactResizeDetector from "react-resize-detector";
import { RInteraction, RLayerVector, RMap, ROSM } from "rlayers";
import { RDraw } from "rlayers/interaction";
import { v4 as uuid } from "uuid";
import { READ_ONLY_MODE } from "../documentDetails/types/readOnlyMode";
import MethodsSubMenu from "../methodsSubMenu";
import { MODE, ZOOM } from "./constants";
import LinesOrAreasLayer from "./linesOrAreasLayer";
import locale from "./map.locale";
import Styled from "./map.styled";
import NamesCluster from "./namesCluster";
import PointsCluster from "./pointsCluster";
import Tooltip from "./tooltip";
import { buildGeoFeatureToDocumentMapDataGeometry, buildGeoFeatureToDocumentMapDataPoint } from "./utils/buildMethods";
import buildOnAddFeatureCallback from "./utils/buildOnAddFeatureCallback";
import buildContextMenuItems from "./utils/contextMenu/buildContextMenuItems";
import getScreenBase64 from "./utils/getScreenBase64";
import getUpdatedFlags from "./utils/getUpdatedFlags";
import { fitMapToPoints, getIds, initialOptions } from "./utils/map.utils";
import { geometryStylesPrimary } from "./utils/styles";
import { createProviderWebsocketProvider } from "Common/api/wsServer";
import { Doc } from "yjs";
import { RemoteCursorsMap, RemoteCursorsProvider } from "Components/RemoteCursors";
import MapPanel from "Areas/Documents/map/mapPanel/mapPanel";
import styles from "Areas/Documents/graph/graph.module.css";
const detailsPanelTableColumns = [COLUMN.OBJECT_TYPE, COLUMN.ID, COLUMN.COLOR];
const conditionRDragBox = (mapBrowserEvent) => {
    var _a;
    return mapBrowserEvent.type === MapBrowserEventType.POINTERDOWN &&
        ((_a = head(mapBrowserEvent.activePointers)) === null || _a === void 0 ? void 0 : _a.buttons) === 2;
};
function Map(props) {
    var _a;
    const { undo, redo, clearHistory, addToHistory, deleted, deletedRef } = useHistory();
    const [hasChanges, setHasChanges] = useState(false);
    const hasChangesRef = useRef(false);
    const sourceDataKey = useMemo(() => `wrapperId${uuid()}`, [props.sourceData]);
    const [provider, setProvider] = useState(null);
    const [mode, setMode] = useState(MODE.VIEW);
    const tooltipRef = useRef(null);
    const pointsClusterRef = useRef(null);
    const linesOrAreasRef = useRef(null);
    const mapRef = useRef(null);
    const shouldFitMap = useRef(undefined);
    const rDrawRef = useRef(null);
    const pointsFeaturesRef = useRef(props.pointsFeatures);
    const sourceDataRef = useRef();
    const dataRef = useRef();
    const wrapperId = useMemo(() => `wrapperId${uuid()}`, []);
    const [height, setHeight] = useState(0);
    const [zoom, setZoom] = useState(((_a = props.mapOptions) === null || _a === void 0 ? void 0 : _a.zoom) || initialOptions.zoom);
    const mapOptionsRef = useRef();
    const startDragBoxRef = useRef();
    const contextMenuRef = useRef(null);
    const readOnlyModeRef = useRef(props.readOnlyMode);
    useEffect(() => {
        hasChangesRef.current = hasChanges;
    }, [hasChanges]);
    useEffect(() => {
        readOnlyModeRef.current = props.readOnlyMode;
    }, [props.readOnlyMode]);
    useBeforeUnloadMessage(hasChangesRef);
    useEffect(() => {
        var _a, _b, _c;
        (_a = tooltipRef.current) === null || _a === void 0 ? void 0 : _a.hide();
        (_c = (_b = contextMenuRef.current) === null || _b === void 0 ? void 0 : _b.hide) === null || _c === void 0 ? void 0 : _c.call(_b);
    }, [props.timelineInterval]);
    useEffect(() => {
        var _a, _b;
        (_a = props.onInitialized) === null || _a === void 0 ? void 0 : _a.call(props);
        if ((_b = mapRef.current) === null || _b === void 0 ? void 0 : _b.ol) {
            mapRef.current.ol.addControl(new Attribution());
            const view = mapRef.current.ol.getView();
            if (view.isDef()) {
                view.on("change:resolution", () => {
                    var _a, _b;
                    (_b = (_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) === null || _b === void 0 ? void 0 : _b.call(_a);
                });
                const changeZoomCallbackDebounced = debounce(() => {
                    var _a, _b, _c;
                    if (view === null || view === void 0 ? void 0 : view.isDef())
                        setZoom(prev => view.getZoom() || prev);
                    (_a = tooltipRef.current) === null || _a === void 0 ? void 0 : _a.hide();
                    (_c = (_b = contextMenuRef.current) === null || _b === void 0 ? void 0 : _b.hide) === null || _c === void 0 ? void 0 : _c.call(_b);
                }, 100);
                view.on("change:resolution", changeZoomCallbackDebounced);
                mapRef.current.ol.getViewport().addEventListener("contextmenu", e => {
                    e.preventDefault();
                });
            }
        }
    }, []);
    useEffect(() => {
        var _a;
        if (shouldFitMap.current === undefined &&
            pointsFeaturesRef.current === undefined &&
            props.pointsFeatures !== undefined) {
            shouldFitMap.current = true;
        }
        pointsFeaturesRef.current = props.pointsFeatures;
        (_a = tooltipRef.current) === null || _a === void 0 ? void 0 : _a.hide();
    }, [props.pointsFeatures]);
    useEffect(() => {
        sourceDataRef.current = props.sourceData;
    }, [props.sourceData]);
    useEffect(() => {
        dataRef.current = props.data;
    }, [props.data]);
    useEffect(() => {
        var _a, _b, _c;
        if (!props.navigateToId || !pointsFeaturesRef.current || !((_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.ol))
            return;
        const feature = find(pointsFeaturesRef.current, ["id", props.navigateToId]);
        if (!feature)
            return;
        fitMapToPoints([feature], mapRef.current.ol);
        props.navigateToId && ((_b = props.onResetNavigateTo) === null || _b === void 0 ? void 0 : _b.call(props));
        props.documentId && ((_c = props.onChangeSelected) === null || _c === void 0 ? void 0 : _c.call(props, props.documentId, [props.navigateToId]));
    }, [props.navigateToId, props.documentId]);
    useEffect(() => {
        clearHistory();
        setHasChanges(false);
    }, [sourceDataKey]);
    const savingCallback = useCallback((documentId) => () => {
        var _a;
        hasChangesRef.current &&
            sourceDataRef.current &&
            dataRef.current &&
            props.canEdit &&
            readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE &&
            ((_a = props.onSaveDocumentMapData) === null || _a === void 0 ? void 0 : _a.call(props, documentId, sourceDataRef.current, {
                ...dataRef.current,
                Points: filter(dataRef.current.Points, p => !includes(deletedRef.current, p.Id)),
                Geometry: filter(dataRef.current.Geometry, p => !includes(deletedRef.current, p.Id))
            }));
    }, [props.onSaveDocumentMapData]);
    useEffect(() => {
        !props.data && props.documentId && props.onGetMapData && props.onGetMapData(props.documentId);
        return () => {
            var _a;
            if (!props.documentId)
                return;
            savingCallback(props.documentId)();
            clearHistory();
            setMode(MODE.VIEW);
            mapOptionsRef.current &&
                ((_a = props.onSetMapOptions) === null || _a === void 0 ? void 0 : _a.call(props, props.documentId, mapOptionsRef.current.center, mapOptionsRef.current.zoom));
        };
    }, [props.documentId]);
    useEffect(() => {
        props.documentId &&
            props.saveTriggered &&
            readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE &&
            savingCallback(props.documentId)();
    }, [props.documentId && props.saveTriggered]);
    const handleChangeZoom = useCallback((newZoom) => {
        var _a, _b;
        const view = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.ol.getView();
        if ((view === null || view === void 0 ? void 0 : view.isDef()) && view.getZoom() !== newZoom) {
            ((_b = contextMenuRef.current) === null || _b === void 0 ? void 0 : _b.hide) && contextMenuRef.current.hide();
            view.setZoom(newZoom);
        }
    }, []);
    const handleUndo = useCallback(() => {
        props.onChangeSelected && props.documentId && props.onChangeSelected(props.documentId, []);
        if (undo) {
            const undoData = undo();
            if (props.documentId && undoData.modifiedData && props.onChangeMapData) {
                const { nameUpdated, colorUpdated, pointsCoordsUpdated } = getUpdatedFlags(undoData.ids, dataRef.current, undoData.modifiedData);
                props.onChangeMapData(props.documentId, undoData.modifiedData, nameUpdated, colorUpdated, pointsCoordsUpdated);
            }
            setHasChanges(undoData.canUndo);
        }
    }, [props.documentId, props.onChangeMapData, props.onChangeSelected, undo]);
    const handleRedo = useCallback(() => {
        var _a;
        props.documentId && ((_a = props.onChangeSelected) === null || _a === void 0 ? void 0 : _a.call(props, props.documentId, []));
        if (redo) {
            const redoData = redo();
            if (props.documentId && redoData.modifiedData && props.onChangeMapData) {
                const { nameUpdated, colorUpdated, pointsCoordsUpdated } = getUpdatedFlags(redoData.ids, dataRef.current, redoData.modifiedData);
                props.onChangeMapData(props.documentId, redoData.modifiedData, nameUpdated, colorUpdated, pointsCoordsUpdated);
            }
            setHasChanges(true);
        }
    }, [props.documentId, props.onChangeMapData, props.onChangeSelected, redo]);
    const handleRefresh = useCallback(() => {
        var _a;
        props.documentId && ((_a = props.onGetMapData) === null || _a === void 0 ? void 0 : _a.call(props, props.documentId));
    }, [props.documentId, props.onGetMapData]);
    const handleDelete = useCallback(() => {
        var _a;
        if (props.canEdit && props.documentId && props.selected && !isEmpty(props.selected)) {
            props.onChangeSelected && props.documentId && props.onChangeSelected(props.documentId, []);
            addToHistory("delete", props.selected);
            setHasChanges(true);
            (_a = tooltipRef.current) === null || _a === void 0 ? void 0 : _a.hide();
        }
    }, [props.documentId, props.selected, props.onChangeSelected]);
    const handleEscape = useCallback(() => {
        setMode(MODE.VIEW);
    }, []);
    useKeyboardEventListener("Delete", false, handleDelete);
    useKeyboardEventListener("Escape", false, handleEscape);
    useEffect(() => {
        var _a;
        (_a = props.onChangeDeleted) === null || _a === void 0 ? void 0 : _a.call(props, deleted);
    }, [deleted]);
    const handleRenderComplete = useCallback((e) => {
        var _a;
        if (shouldFitMap.current) {
            shouldFitMap.current = false;
            pointsFeaturesRef.current && fitMapToPoints(pointsFeaturesRef.current, e.target);
        }
        else {
            const view = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.ol.getView();
            const viewCenter = view === null || view === void 0 ? void 0 : view.getCenter();
            const viewZoom = view === null || view === void 0 ? void 0 : view.getZoom();
            if (viewCenter && viewZoom) {
                mapOptionsRef.current = {
                    center: viewCenter,
                    zoom: viewZoom
                };
            }
        }
    }, []);
    const handleMapSizeChanged = useCallback((_, h) => {
        var _a;
        !isUndefined(h) && setHeight(h);
        ((_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) && contextMenuRef.current.hide();
        window.dispatchEvent(new Event("resize"));
    }, []);
    const handleDataUpdate = useCallback((ids, fieldName, value) => {
        if (!props.documentId || !props.onChangeMapData)
            return [];
        const toUpdateNodeSourceIds = new Set(map(filter(pointsFeaturesRef.current, e => ids.has(e.id) && Boolean(e.nodeSource)), "nodeSource.NodeId"));
        const toUpdateFeatures = filter(pointsFeaturesRef.current, d => {
            var _a;
            return Boolean(ids.has(d.id) || (((_a = d.nodeSource) === null || _a === void 0 ? void 0 : _a.NodeId) && toUpdateNodeSourceIds.has(d.nodeSource.NodeId)));
        });
        if (isEmpty(toUpdateFeatures))
            return [];
        const toUpdate = [];
        const updated = compact(map(toUpdateFeatures, e => {
            const point = buildGeoFeatureToDocumentMapDataPoint(e);
            if (point && point.NodeSource) {
                toUpdate.push(cloneDeep(point));
                point.NodeSource = {
                    ...point.NodeSource,
                    [fieldName]: value
                };
                return point;
            }
            const geometry = buildGeoFeatureToDocumentMapDataGeometry(e);
            if (geometry) {
                toUpdate.push(cloneDeep(geometry));
                geometry[fieldName] = value;
                return geometry;
            }
            return undefined;
        }));
        addToHistory("update", map(toUpdateFeatures, "id"), toUpdate, updated);
        props.onChangeMapData(props.documentId, updated, fieldName === "Name", fieldName === "Color", fieldName === "Points");
        setHasChanges(true);
        return updated;
    }, [props.documentId, props.onChangeMapData]);
    const handlePointerEnterOrLeave = useCallback((eventType, pixel, ids) => {
        if (!tooltipRef.current)
            return;
        if (eventType === "pointerenter") {
            tooltipRef.current.addData(filter(pointsFeaturesRef.current, item => includes(ids, item.id)), pixel);
        }
        else {
            tooltipRef.current.removeData(ids);
        }
    }, []);
    const handlePointerLineOrAreaEnterOrLeave = useCallback((eventType, pixel, id) => {
        if (!tooltipRef.current)
            return;
        if (eventType === "pointerenter") {
            const fullData = find(pointsFeaturesRef.current, ["id", id]);
            fullData && tooltipRef.current.addData([fullData], pixel);
        }
        else {
            tooltipRef.current.removeData([id]);
        }
    }, []);
    const handleSingleClick = useCallback((e) => {
        var _a;
        const features = e.map.getFeaturesAtPixel(e.pixel);
        const ids = getIds(features);
        props.documentId && ((_a = props.onChangeSelected) === null || _a === void 0 ? void 0 : _a.call(props, props.documentId, ids));
    }, [props.onChangeSelected, props.documentId, mode]);
    const handleBoxStart = useCallback((e) => {
        var _a, _b;
        startDragBoxRef.current = e.coordinate;
        e.mapBrowserEvent.preventDefault();
        (_b = (_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, []);
    const handleBoxEnd = useCallback((e) => {
        var _a;
        if (linesOrAreasRef.current && pointsClusterRef.current && startDragBoxRef.current) {
            const extent = boundingExtent([startDragBoxRef.current, e.coordinate]);
            startDragBoxRef.current = undefined;
            let ids = getIds(pointsClusterRef.current.source.getFeaturesInExtent(extent));
            linesOrAreasRef.current.source.forEachFeatureIntersectingExtent(extent, f => {
                ids = ids.concat(getIds([f]));
            });
            props.documentId && ((_a = props.onChangeSelected) === null || _a === void 0 ? void 0 : _a.call(props, props.documentId, ids));
        }
    }, [props.onChangeSelected, props.documentId]);
    const getMapScreen = useCallback(async () => {
        var _a;
        if (!mapRef.current)
            return undefined;
        const base64 = await getScreenBase64(mapRef.current.ol);
        if (!base64) {
            (_a = props.showError) === null || _a === void 0 ? void 0 : _a.call(props, locale.GENERATE_SCREEN_ERROR_MESSAGE);
        }
        return base64;
    }, [props.showError]);
    const containerStyle = useMemo(() => ({ paddingBottom: props.bottomOffset }), [props.bottomOffset]);
    const handleContextMenu = useCallback((e) => {
        var _a, _b, _c, _d;
        if (!props.documentId ||
            !props.documentObjects ||
            !startDragBoxRef.current ||
            !mapRef.current ||
            !((_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.show) ||
            !((_b = contextMenuRef.current) === null || _b === void 0 ? void 0 : _b.setMenuItems))
            return;
        let cursorOnEmptySpace = true;
        const features = mapRef.current.ol.getFeaturesAtPixel(mapRef.current.ol.getEventPixel(e));
        if (features.length && ((_c = props.detailedPanelData) === null || _c === void 0 ? void 0 : _c.length)) {
            const underMouseIds = getIds(features);
            const selectedIds = map(props.detailedPanelData, getId);
            cursorOnEmptySpace = difference(underMouseIds, selectedIds).length === underMouseIds.length;
        }
        const menuItems = buildContextMenuItems(props.detailedPanelData, cursorOnEmptySpace, handleDelete, (key) => (React.createElement(MethodsSubMenu, { key: key, onBeforeRunMethods: () => {
                var _a, _b;
                ((_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) && ((_b = contextMenuRef.current) === null || _b === void 0 ? void 0 : _b.hide());
            } })), props.onSendToGraph
            ? (geometry) => {
                props.documentId &&
                    props.documentObjects &&
                    props.onSendToGraph &&
                    props.onSendToGraph(props.documentId, geometry, props.documentObjects);
            }
            : undefined);
        if (menuItems && menuItems.length) {
            (_d = tooltipRef.current) === null || _d === void 0 ? void 0 : _d.hide();
            contextMenuRef.current.setMenuItems(menuItems);
            contextMenuRef.current.show(e.clientX, e.clientY);
        }
    }, [
        props.documentId,
        props.documentObjects,
        props.detailedPanelData,
        handleDelete,
        props.onSendToGraph
    ]);
    const handleMouseLeave = useCallback(() => {
        var _a;
        (_a = tooltipRef.current) === null || _a === void 0 ? void 0 : _a.hide();
    }, []);
    const handlePointerDrag = useCallback(() => {
        var _a;
        (_a = tooltipRef.current) === null || _a === void 0 ? void 0 : _a.hide();
    }, []);
    const handleGetPreview = useCallback((_, __, ___, largeAttributeId, completed, rejected) => {
        if (largeAttributeId) {
            props.onGetPreview(largeAttributeId, completed, rejected);
        }
        else {
            rejected && rejected();
        }
    }, [props.onGetPreview]);
    const handleTooltipSelect = useCallback((ids) => {
        props.documentId && props.onChangeSelected && props.onChangeSelected(props.documentId, ids);
    }, [props.documentId, props.onChangeSelected]);
    const handleAddFeature = useCallback(buildOnAddFeatureCallback(props.documentId, (documentId, data) => {
        var _a, _b;
        (_a = props.onAddGeometry) === null || _a === void 0 ? void 0 : _a.call(props, documentId, data);
        data.Id && addToHistory("add", [data.Id]);
        setHasChanges(true);
        if (props.readOnlyMode === "readWrite" &&
            mode === MODE.ADDING_LOCATION_POINT_ON_GRAPH &&
            props.documentObjects) {
            (_b = props.onSendToGraph) === null || _b === void 0 ? void 0 : _b.call(props, documentId, data, props.documentObjects);
        }
    }), [props.documentId, props.onAddGeometry, addToHistory, mode, props.documentObjects]);
    const conditionRDraw = useCallback((mapBrowserEvent) => {
        var _a, _b, _c;
        if (((_a = head(mapBrowserEvent.activePointers)) === null || _a === void 0 ? void 0 : _a.buttons) !== 1) {
            (_b = rDrawRef.current) === null || _b === void 0 ? void 0 : _b.ol.finishDrawing();
        }
        return (mapBrowserEvent.type === MapBrowserEventType.POINTERDOWN &&
            ((_c = head(mapBrowserEvent.activePointers)) === null || _c === void 0 ? void 0 : _c.buttons) === 1);
    }, []);
    useEffect(() => {
        props.onInProgressChanged && props.onInProgressChanged(Boolean(props.inProgress));
    }, [props.inProgress]);
    useEffect(() => {
        const doc = new Doc();
        const roomName = `map_${props.documentId}`;
        const p = createProviderWebsocketProvider(roomName, doc);
        setProvider(p);
        return () => p === null || p === void 0 ? void 0 : p.destroy();
    }, [props.documentId]);
    return (React.createElement("div", { className: styles.container, style: containerStyle },
        React.createElement(MapPanel, { rowActionsPanelHeight: props.rowActionsPanelHeight, rightSidePanelOpen: props.calculatedDetailedPanelOpen, zoom: zoom, onChangeZoom: handleChangeZoom, mode: mode, readOnlyMode: props.readOnlyMode, onChangeMode: props.canEdit ? setMode : undefined, onDelete: !isEmpty(props.selected) && props.canEdit ? handleDelete : undefined, onUndo: undo && props.canEdit ? handleUndo : undefined, onRedo: redo && props.canEdit ? handleRedo : undefined, onSave: props.documentId &&
                hasChanges &&
                props.canEdit &&
                props.readOnlyMode === READ_ONLY_MODE.READ_WRITE
                ? savingCallback(props.documentId)
                : undefined, onRefresh: handleRefresh, getSummaryReportObjectValue: getMapScreen }),
        React.createElement(RightSideDetailsPanel, { openedHeight: height ? height + (props.bottomOffset || 0) : undefined, closedHeight: props.rowActionsPanelHeight, onChangeOpen: props.onChangeDetailedPanelOpen, documentId: props.documentId, open: props.calculatedDetailedPanelOpen, data: props.detailedPanelData, onDataUpdate: props.canEdit ? handleDataUpdate : undefined, canModifyAttributes: false, tableColumns: detailsPanelTableColumns, documentObjects: props.documentObjects, onGetPreview: handleGetPreview }),
        React.createElement(ProgressOverlay, { show: props.inProgress },
            React.createElement("div", { className: "position-relative stretched-to-fit", id: wrapperId, onContextMenu: handleContextMenu, onMouseLeave: handleMouseLeave },
                React.createElement(ReactResizeDetector, { render: () => undefined, querySelector: `#${wrapperId}`, handleWidth: true, handleHeight: true, onResize: handleMapSizeChanged, refreshMode: "throttle", refreshRate: 50 }),
                React.createElement(RemoteCursorsProvider, { provider: provider }, useMemo(() => (React.createElement(RMap, { ref: mapRef, minZoom: ZOOM.MIN, maxZoom: ZOOM.MAX, noDefaultControls: true, initial: props.mapOptions || initialOptions, onRenderComplete: handleRenderComplete, onSingleClick: mode === MODE.VIEW ? handleSingleClick : undefined, onPointerDrag: handlePointerDrag },
                    React.createElement(ROSM, null),
                    React.createElement(RemoteCursorsMap, null),
                    React.createElement(LinesOrAreasLayer, { key: sourceDataKey, layerRef: linesOrAreasRef, onPointerEnterOrLeave: mode === MODE.VIEW ? handlePointerLineOrAreaEnterOrLeave : undefined, deleted: deleted }),
                    React.createElement(NamesCluster, { deleted: deleted }),
                    React.createElement(PointsCluster, { layerRef: pointsClusterRef, deleted: deleted, onPointerEnterOrLeave: mode === MODE.VIEW ? handlePointerEnterOrLeave : undefined }),
                    mode !== MODE.VIEW && props.canEdit ? (React.createElement(RLayerVector, { onAddFeature: handleAddFeature },
                        (mode === MODE.ADDING_LOCATION_POINT ||
                            mode === MODE.ADDING_LOCATION_POINT_ON_GRAPH) && (React.createElement(RDraw, { type: "Point", condition: conditionRDraw, freehandCondition: never, style: geometryStylesPrimary, ref: rDrawRef })),
                        mode === MODE.ADDING_LOCATION_AREA && (React.createElement(RDraw, { type: "Polygon", condition: conditionRDraw, freehandCondition: shiftKeyOnly, style: geometryStylesPrimary, ref: rDrawRef })),
                        mode === MODE.ADDING_LOCATION_LINE && (React.createElement(RDraw, { type: "MultiLineString", condition: conditionRDraw, freehandCondition: never, style: geometryStylesPrimary, ref: rDrawRef })))) : (React.createElement(RInteraction.RDragBox, { condition: conditionRDragBox, onBoxStart: handleBoxStart, onBoxEnd: handleBoxEnd })))), [
                    sourceDataKey,
                    handlePointerLineOrAreaEnterOrLeave,
                    handleRenderComplete,
                    handleSingleClick,
                    handlePointerEnterOrLeave,
                    handleBoxStart,
                    handleBoxEnd,
                    handlePointerDrag,
                    mode,
                    props.onAddGeometry,
                    props.documentId,
                    deleted
                ])))),
        React.createElement(Tooltip, { tooltipRef: tooltipRef, targetId: wrapperId, documentObjects: props.documentObjects, onSelect: handleTooltipSelect }),
        React.createElement(Styled.ModeHintMessage, { mode: mode }),
        props.canEdit && (React.createElement(Menu, { mode: "contextmenu", zIndex: parseInt(themeVariables["zindex-pageheader-menu"]) + 1, menuRef: contextMenuRef }))));
}
export default memo(Map);
